import React, { useState, useMemo, useCallback } from 'react';
import { Plus, StickyNote, Send } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import EmptyState from '@shared/ui/EmptyState';
import { NoteV2, NoteReply } from '../../services/notesV2Api';
import NotesV2ApiService from '../../services/notesV2Api';
import NoteFormModal from './NoteFormModal';
import NoteCard, { NoteCardData } from './NoteCard';
import ShareNoteModal from './ShareNoteModal';
import ConfirmationDialog from '../ConfirmationDialog';
import { useWorkspaceNotesV2 } from '../../hooks/useNotesV2';
import { isOrgAdminOfOwnerOrg, useWorkspacePermissions } from '../../hooks/useWorkspacePermissions';
import Button from '@shared/ui/Button';
import toast from 'react-hot-toast';

// Re-export NoteCardData as Note for backward compatibility
type Note = NoteCardData;

interface ViewNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  notes: Note[];
  entityType: string;
  entityId: string;
  entityDisplayName?: string;
  workspaceId?: string;
}

const ViewNotesModal: React.FC<ViewNotesModalProps> = ({
  isOpen,
  onClose,
  notes,
  entityType,
  entityId,
  entityDisplayName,
  workspaceId
}) => {
  const [editingNote, setEditingNote] = useState<NoteV2 | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [showCreateNote, setShowCreateNote] = useState(false);
  const [sharingNoteId, setSharingNoteId] = useState<string | null>(null);
  const [sharingNoteTitle, setSharingNoteTitle] = useState<string | undefined>(undefined);
  
  // Reply state
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
  const [replies, setReplies] = useState<Record<string, NoteReply[]>>({});
  const [loadingReplies, setLoadingReplies] = useState<string | null>(null);
  const [replyingToNoteId, setReplyingToNoteId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  
  // Only initialize hooks if we have workspaceId (edit/delete only works in workspace context)
  const { deleteNote, createNote, isCreating } = useWorkspaceNotesV2(workspaceId || '', {}, 1, 50);

  /*
    Fetched here rather than passed in: this modal is mounted from 19 places
    across 12 files, several of which (report views, the org-settings reports
    tab, LegislationPage) hold no workspace permissions object to hand down.
  */
  const { permissions: workspacePermissions } = useWorkspacePermissions(workspaceId);
  const canDeleteAnyNote = isOrgAdminOfOwnerOrg(workspacePermissions);

  // Get current user from localStorage to check note authorship
  const currentUser = useMemo(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        username: userData.username || null,
        displayName: userData.displayName || userData.firstName || null
      };
    } catch {
      return { username: null, displayName: null };
    }
  }, []);

  // Helper to check if the current user wrote something. Notes and replies
  // arrive as different shapes, so the check takes the author name itself.
  const isAuthorName = useCallback((authorName?: string): boolean => {
    if (!currentUser.username && !currentUser.displayName) return false;
    // author_name could be either the username or the display name
    return authorName === currentUser.username || authorName === currentUser.displayName;
  }, [currentUser]);

  const isNoteAuthor = useCallback(
    (note: Note): boolean => isAuthorName(note.author_name),
    [isAuthorName]
  );

  const handleEditNote = (note: Note) => {
    // Convert Note to NoteV2 format for editing
    const noteV2: NoteV2 = {
      id: note.id,
      title: note.title,
      content: note.content,
      include_in_reports: note.include_in_reports,
      created_at: note.created_at,
      updated_at: note.updated_at,
      version: 1,
      author: {
        username: note.author_name,
        display_name: note.author_name
      },
      entities: [{
        entity_type: entityType,
        entity_type_display: entityType,
        entity_id: entityId,
        entity_display_name: entityDisplayName || `${entityType}: ${entityId}`
      }],
      tags: note.tags
    };
    setEditingNote(noteV2);
  };

  const handleDeleteNote = (noteId: string) => {
    setDeletingNoteId(noteId);
  };

  const confirmDelete = () => {
    if (deletingNoteId && workspaceId) {
      deleteNote(deletingNoteId, {
        onSuccess: () => {
          setDeletingNoteId(null);
        }
      });
    }
  };

  const handleCreateNote = (noteData: any) => {
    if (workspaceId) {
      createNote(noteData, {
        onSuccess: () => {
          setShowCreateNote(false);
        }
      });
    }
  };

  const handleShareNote = (noteId: string) => {
    const note = notes.find(n => n.id === noteId);
    setSharingNoteId(noteId);
    setSharingNoteTitle(note?.title);
  };

  // Fetch replies for a note
  const fetchReplies = async (noteId: string) => {
    try {
      setLoadingReplies(noteId);
      const noteReplies = await NotesV2ApiService.getNoteReplies(noteId);
      setReplies(prev => ({ ...prev, [noteId]: noteReplies }));
    } catch (error) {
      console.error('Error fetching replies:', error);
      toast.error('Failed to load replies');
    } finally {
      setLoadingReplies(null);
    }
  };

  // Handle viewing replies
  const handleViewReplies = async (noteId: string) => {
    if (expandedNoteId === noteId) {
      setExpandedNoteId(null);
    } else {
      setExpandedNoteId(noteId);
      if (!replies[noteId]) {
        await fetchReplies(noteId);
      }
    }
  };

  // Handle reply button click
  const handleReply = (noteId: string) => {
    setReplyingToNoteId(noteId);
    setReplyContent('');
    // Also expand to show existing replies
    if (!expandedNoteId) {
      setExpandedNoteId(noteId);
      if (!replies[noteId]) {
        fetchReplies(noteId);
      }
    }
  };

  // Submit reply
  const handleSubmitReply = async () => {
    if (!replyingToNoteId || !replyContent.trim()) return;

    try {
      setSubmittingReply(true);
      const newReply = await NotesV2ApiService.createReply(replyingToNoteId, {
        content: replyContent.trim(),
        include_in_reports: false
      });
      
      // Add to replies list
      setReplies(prev => ({
        ...prev,
        [replyingToNoteId]: [...(prev[replyingToNoteId] || []), newReply]
      }));
      
      // Reset form
      setReplyingToNoteId(null);
      setReplyContent('');
      toast.success('Reply added');
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error('Failed to add reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const hasNotes = notes.length > 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        title={`Notes for ${entityDisplayName || entityId}`}
        description={`${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`}
        onClose={onClose}
        size="xl"
        footer={
          workspaceId ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreateNote(true)}
              icon={Plus}
              ariaLabel="Create new note"
            >
              New note
            </Button>
          ) : undefined
        }
      >
        {hasNotes ? (
          <div className="space-y-4">
            {notes.map((note) => (
              <div key={note.id}>
                <NoteCard
                  note={note}
                  /*
                    Edit is authorship, full stop — nobody edits someone else's
                    words. Delete additionally allows an ORG ADMIN of the owner
                    org, matching services/notePermissions.js exactly, so the UI
                    neither offers an action the API refuses nor hides one it
                    allows. Note this is narrower than canManage: a workspace
                    `owner` does NOT get it, since that role is being retired.
                  */
                  onEdit={workspaceId && isNoteAuthor(note) ? handleEditNote : undefined}
                  onDelete={
                    workspaceId && (isNoteAuthor(note) || canDeleteAnyNote)
                      ? handleDeleteNote
                      : undefined
                  }
                  onShare={workspaceId ? handleShareNote : undefined}
                  onReply={workspaceId ? handleReply : undefined}
                  onViewReplies={handleViewReplies}
                  showActions={!!workspaceId}
                />

                {expandedNoteId === note.id && (
                  <div className="mt-2 space-y-2">
                    {loadingReplies === note.id && (
                      <div className="ml-6 flex justify-center py-4">
                        <LoadingSpinner size="md" />
                      </div>
                    )}

                    {replies[note.id]?.map((reply) => (
                      <NoteCard
                        key={reply.id}
                        note={{
                          id: reply.id,
                          title: reply.title,
                          content: reply.content,
                          created_at: reply.created_at,
                          updated_at: reply.updated_at,
                          include_in_reports: reply.include_in_reports,
                          author_name: reply.author_name,
                          tags: reply.tags,
                          reply_count: reply.reply_count
                        }}
                        isReply={true}
                        showActions={!!workspaceId}
                        onDelete={
                          workspaceId && (isAuthorName(reply.author_name) || canDeleteAnyNote)
                            ? handleDeleteNote
                            : undefined
                        }
                      />
                    ))}

                    {replyingToNoteId === note.id && workspaceId && (
                      <div className="surface-inset ml-6">
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Write your reply..."
                          rows={3}
                          className="input resize-none text-sm"
                          maxLength={10000}
                        />
                        <div className="mt-2 flex justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setReplyingToNoteId(null);
                              setReplyContent('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            icon={Send}
                            onClick={handleSubmitReply}
                            loading={submittingReply}
                            disabled={submittingReply || !replyContent.trim()}
                          >
                            Reply
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={StickyNote}
            title="No notes yet"
            body={`Nothing has been written about this ${entityType} so far.`}
            action={
              workspaceId ? (
                <Button variant="primary" onClick={() => setShowCreateNote(true)} icon={Plus}>
                  Create first note
                </Button>
              ) : undefined
            }
          />
        )}
      </Modal>

      {/* Create Note Modal */}
      {showCreateNote && workspaceId && (
        <NoteFormModal
          isOpen={showCreateNote}
          onClose={() => setShowCreateNote(false)}
          workspaceId={workspaceId}
          onSave={handleCreateNote}
          isSaving={isCreating}
          defaultEntities={[
            {
              entity_type: entityType,
              entity_id: entityId,
              entity_display_name: entityDisplayName || `${entityType}: ${entityId}`
            }
          ]}
        />
      )}

      {/* Edit Note Modal */}
      {editingNote && workspaceId && (
        <NoteFormModal
          isOpen={!!editingNote}
          onClose={() => setEditingNote(null)}
          workspaceId={workspaceId}
          note={editingNote}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!deletingNoteId}
        onConfirm={confirmDelete}
        onCancel={() => setDeletingNoteId(null)}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Share Note Modal */}
      {sharingNoteId && (
        <ShareNoteModal
          isOpen={!!sharingNoteId}
          onClose={() => {
            setSharingNoteId(null);
            setSharingNoteTitle(undefined);
          }}
          noteId={sharingNoteId}
          noteTitle={sharingNoteTitle}
        />
      )}
    </>
  );
};

export default ViewNotesModal;
